import { withCustomBackgroundMapColorDefaults } from "@/constants/backgroundMapCustomStyle";
import type { LayerUpdateControllerProp } from "@/overlay/components/SmartForm/LayerForms/LayerFormMapping";
import { useAppDispatch } from "@/reducer";
import { setLayersProps } from "@/reducer/MapsState";
import type { BackgroundMapLayer } from "@mm/metx-workbench.meteomatics.com";
import type { SubmitHandler } from "react-hook-form";
import {
  CartographicMaterialUpdateForm,
  type CartographicMaterialUpdateFormData,
} from "../view/CartographicMaterialLayerUpdateForm";

export function buildBackgroundMapUpdatePatch(
  layer: BackgroundMapLayer,
  layerDataPatch: Partial<CartographicMaterialUpdateFormData["layerData"]>,
): Partial<CartographicMaterialUpdateFormData["layerData"]> {
  const style = layerDataPatch.style ?? layer.style;
  const customOptions = withCustomBackgroundMapColorDefaults(
    style,
    layerDataPatch.custom_options ?? layer.custom_options,
  );
  const normalizedCustomOptions: CartographicMaterialUpdateFormData["layerData"]["custom_options"] = {};

  if (customOptions.map_label_language != null) {
    normalizedCustomOptions.map_label_language = customOptions.map_label_language;
  }
  if (customOptions.show_state_border != null) {
    normalizedCustomOptions.show_state_border = customOptions.show_state_border;
  }
  if (customOptions.line_color != null) {
    normalizedCustomOptions.line_color = customOptions.line_color;
  }
  if (customOptions.custom_background_color != null) {
    normalizedCustomOptions.custom_background_color = customOptions.custom_background_color;
  }
  if (customOptions.custom_water_color != null) {
    normalizedCustomOptions.custom_water_color = customOptions.custom_water_color;
  }
  if (customOptions.custom_border_color != null) {
    normalizedCustomOptions.custom_border_color = customOptions.custom_border_color;
  }

  return {
    ...layerDataPatch,
    style,
    custom_options: normalizedCustomOptions,
  };
}

/**
 * Handles all the logic for map background implementation update.
 * Note:
 * This is a controller, meaning that we should generally NOT delegate layer creation logic
 * to the parent component by passing callback from parent etc.
 */
export function CartographicMaterialLayerUpdateController(props: LayerUpdateControllerProp<BackgroundMapLayer>) {
  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<CartographicMaterialUpdateFormData> = (formData) => {
    const nextLayerData = buildBackgroundMapUpdatePatch(props.layer, formData.layerData);
    dispatch(setLayersProps({ layerId: props.layer.id, id: props.mapId.map, props: nextLayerData }));
  };

  return (
    <CartographicMaterialUpdateForm
      defaultFormData={props.layer}
      onSubmit={onSubmit}
      submitMode="onChange"
      showAllConfig={true}
    />
  );
}
